import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "./authStore";
import { atlasWs } from "../api/ws";
import { decodeJwtPayload, exchangeCode, validateIdToken } from "./pkce";
import type { LoginResponse } from "../types/common";

const AUTH_ISSUER  = import.meta.env.VITE_AUTH_ISSUER  ?? "https://auth.mavr.finance";
const OAUTH_CLIENT = import.meta.env.VITE_OAUTH_CLIENT_ID ?? "mavr-atlas-web";
const REDIRECT_URI = `${window.location.origin}/auth/oidc/callback`;

export function OidcCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const setFromLogin   = useAuthStore((s) => s.setFromLogin);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The issuer redirects back with an OAuth error instead of a code when it
    // cannot authenticate the user (e.g. login_required for a future
    // prompt=none silent-SSO attempt, or access_denied).
    const oidcError = searchParams.get("error");
    if (oidcError) {
      setError(
        searchParams.get("error_description") ?? `Sign-in was not completed (${oidcError})`,
      );
      return;
    }

    const code  = searchParams.get("code")  ?? "";
    const state = searchParams.get("state") ?? "";

    if (!code) {
      setError("Missing authorization code");
      return;
    }

    void (async () => {
      try {
        const tokens = await exchangeCode(AUTH_ISSUER, OAUTH_CLIENT, code, state, REDIRECT_URI);

        // Binding checks on the issuer's ID token (iss/aud/nonce). Optional only
        // while the deployed issuer generation does not mint one yet.
        validateIdToken(tokens.id_token, AUTH_ISSUER, OAUTH_CLIENT);

        // Decode JWT (no sig check — pks-bff validates it on every API call)
        const payload = decodeJwtPayload(tokens.access_token);
        const now     = Math.floor(Date.now() / 1000);

        const synthetic: LoginResponse = {
          access_token:      tokens.access_token,
          refresh_token:     "",
          access_expires_at:  (payload.exp as number) ?? (now + tokens.expires_in),
          refresh_expires_at: now + 86400,
          requires_2fa:       false,
          partial_token:      "",
          claims: {
            user_id:         (payload.sub  as string) ?? "",
            username:        ((payload.email as string) ?? "").split("@")[0],
            email:           (payload.email as string) ?? "",
            roles:           (payload.roles as string[]) ?? [],
            scopes:          (payload.scopes as string[]) ?? [],
            entity_ids:      (payload.entity_ids as string[]) ?? [],
            book_ids:        (payload.book_ids as string[]) ?? [],
            book_trade_ids:  (payload.book_trade_ids as string[]) ?? [],
            desk_ids:        (payload.desk_ids as string[]) ?? [],
            tier:            (payload.tier as string) ?? "monitor",
          },
        };

        setFromLogin(synthetic);
        atlasWs.connect();
        await navigate("/", { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : "OIDC login failed");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      {error ? (
        <div className="text-center">
          <p className="text-sm text-bad mb-4">{error}</p>
          <a href="/login" className="text-xs text-muted underline">
            Back to login
          </a>
        </div>
      ) : (
        <p className="font-mono text-sm text-muted">Completing sign-in…</p>
      )}
    </div>
  );
}
